import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LoopIcon from "@mui/icons-material/Loop";
import SaveAsOutlinedIcon from "@mui/icons-material/SaveAsOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { Button, ButtonGroup, Tooltip } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectIsDirty } from "../../app/slices/TrainingSlice";
import { selectLocalChanges } from "../../app/slices/WebSocketSlice";
import { useSaveTraining } from "../../hooks/SaveTrainingHook";
import {
  onboardingTarget,
  OnboardingTarget,
} from "../onboarding/onboardingTargets";
import LocalChangesModal from "../send_to_unity/LocalChangesModal";
import SaveTrainingAsDialog from "./SaveTrainingAsDialog";
import "./TrainingActionbarSaveButton.css";

type SaveState = "saving" | "dirty" | "saved";

function formatRelativeTime(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

// Same pill language as TestTrainingSplitButton, always in the blue variant.
const PILL_SX = {
  textTransform: "none" as const,
  fontWeight: 600,
  fontSize: "13px",
  height: 32,
  minWidth: 0,
  border: "1.5px solid",
  background: "transparent",
  transition:
    "background 200ms ease, color 200ms ease, border-color 200ms ease",
  "&:hover": {
    background: "color-mix(in srgb, var(--pi-key-blue) 10%, transparent)",
    color: "var(--pi-key-blue)",
    borderColor: "var(--pi-key-blue)",
  },
  "&:first-of-type": {
    borderTopLeftRadius: "5px",
    borderBottomLeftRadius: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    gap: "6px",
  },
  "&:last-of-type": {
    borderTopRightRadius: "5px",
    borderBottomRightRadius: "5px",
    paddingLeft: "4px",
    paddingRight: "6px",
    minWidth: 28,
  },
  "&.Mui-disabled": {
    borderColor: "var(--pi-key-blue)",
    color: "var(--pi-key-blue)",
    opacity: 0.7,
    background: "transparent",
  },
} as const;

export default function TrainingActionbarSaveButton() {
  const { t } = useTranslation();
  const isDirty = useSelector(selectIsDirty);
  const localChanges = useSelector(selectLocalChanges);
  const { saveTraining } = useSaveTraining();
  const [isSaving, setIsSaving] = useState(false);
  const [isLocalChangesModalOpen, setIsLocalChangesModalOpen] = useState(false);
  const [isSaveAsDialogOpen, setIsSaveAsDialogOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const wasDirtyRef = useRef<boolean>(isDirty);

  // Capture the moment isDirty transitions from true → false (a save just landed).
  useEffect(() => {
    if (wasDirtyRef.current && !isDirty) {
      setLastSavedAt(Date.now());
    }
    wasDirtyRef.current = isDirty;
  }, [isDirty]);

  const onSave = useCallback(async () => {
    if (isSaving) return;
    if (Object.keys(localChanges).length > 0) {
      setIsLocalChangesModalOpen(true);
      return;
    }
    setIsSaving(true);
    try {
      await saveTraining();
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, localChanges, saveTraining]);

  const onSaveAs = () => {
    setIsMenuOpen(false);
    if (Object.keys(localChanges).length > 0) {
      setIsLocalChangesModalOpen(true);
      return;
    }

    setIsSaveAsDialogOpen(true);
  };

  useEffect(() => {
    const handleSaveShortcut = (event: KeyboardEvent) => {
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key.toLowerCase() === "s" &&
        !event.repeat
      ) {
        event.preventDefault();
        void onSave();
      }
    };

    window.addEventListener("keydown", handleSaveShortcut);
    return () => {
      window.removeEventListener("keydown", handleSaveShortcut);
    };
  }, [onSave]);

  const state: SaveState = isSaving ? "saving" : isDirty ? "dirty" : "saved";

  const stateContent: Record<SaveState, { icon: JSX.Element; label: string }> = {
    saving: {
      icon: (
        <LoopIcon
          className="trainingactionbar-savebtn-spin"
          sx={{ fontSize: 16 }}
        />
      ),
      label: t("saving"),
    },
    dirty: { icon: <SaveOutlinedIcon sx={{ fontSize: 16 }} />, label: t("save") },
    saved: {
      icon: <CheckCircleOutlinedIcon sx={{ fontSize: 16 }} />,
      label: t("saved"),
    },
  };

  const { icon, label } = stateContent[state];

  const showSavedTooltip = state === "saved" && lastSavedAt !== null;

  return (
    <>
      <ButtonGroup
        ref={anchorRef}
        aria-label="save training split button"
        disableElevation
      >
        <Tooltip
          title={
            showSavedTooltip ? formatRelativeTime(Date.now() - lastSavedAt!) : ""
          }
          enterDelay={200}
        >
          <Button
            variant="piBlueOutlined"
            onClick={isSaving ? undefined : onSave}
            disabled={isSaving}
            className={`trainingactionbar-savebtn trainingactionbar-savebtn--${state}`}
            sx={PILL_SX}
            {...onboardingTarget(OnboardingTarget.Editor360SaveButton)}
          >
            {icon}
            <span className="trainingactionbar-savebtn-label">
              <span data-testid="save-button-label">{label}</span>
              {/* Invisible copies reserve the widest label's width so the button
                  (and the width-matched dropdown) never resize between states. */}
              <span aria-hidden className="trainingactionbar-savebtn-label-ghost">
                {stateContent.dirty.label}
              </span>
              <span aria-hidden className="trainingactionbar-savebtn-label-ghost">
                {stateContent.saved.label}
              </span>
              <span aria-hidden className="trainingactionbar-savebtn-label-ghost">
                {stateContent.saving.label}
              </span>
            </span>
          </Button>
        </Tooltip>
        <Button
          variant="piBlueOutlined"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          disabled={isSaving}
          aria-label={t("save_options") as string}
          aria-haspopup="menu"
          aria-expanded={isMenuOpen ? "true" : undefined}
          sx={PILL_SX}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
        </Button>
      </ButtonGroup>

      <Popper
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        open={isMenuOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-start" ? "left top" : "left bottom",
              transform: "translate(0, 8px)",
              borderRadius: "5px",
            }}
          >
            <Paper
              sx={{
                borderRadius: "5px",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
              }}
            >
              <ClickAwayListener onClickAway={() => setIsMenuOpen(false)}>
                <MenuList
                  className="pipopper-menu"
                  autoFocusItem
                  style={{ border: "none" }}
                >
                  <MenuItem
                    data-testid="save-as-menu-item"
                    aria-label={t("saveAs") as string}
                    onClick={onSaveAs}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      fontFamily: "Poppins",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--pi-text-color-1)",
                      padding: "8px 10px",
                      "&:hover": {
                        backgroundColor: "var(--pi-main-hover)",
                      },
                    }}
                  >
                    <SaveAsOutlinedIcon
                      sx={{ fontSize: 16, color: "var(--pi-key-blue)" }}
                    />
                    <span>{t("saveAs")}</span>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <SaveTrainingAsDialog
        open={isSaveAsDialogOpen}
        onClose={() => setIsSaveAsDialogOpen(false)}
      />
      <LocalChangesModal
        isOpen={isLocalChangesModalOpen}
        onClose={() => setIsLocalChangesModalOpen(false)}
        title="Local Changes"
        width="750px"
        height="calc(50vh / var(--app-zoom, 1))"
      />
    </>
  );
}
